import { useCallback, useMemo, useState } from 'react';
import { useAcademicEvents, parseDateRangeEnd } from './useAcademicCalendar';
import { usePrograms, useIsLoading } from '@/store/useStore';
import { hasCompletedFinalSemester, normalizeSemester } from '@/utils/programMatching';
import type { UserPreferences } from '@/types';

const STORAGE_KEY = 'graduationNoticeSeen';

export function isFinalSemester(semester?: string | null): boolean {
  return normalizeSemester(semester) === 'VIII';
}

export interface SemesterCompletion {
  /** Human label pulled from the calendar, e.g. "Spring 2026". */
  label: string;
  /** Last day of that semester's final term examination. */
  endedOn: Date;
}

/**
 * Most recent semester whose final term examination has finished, according to
 * public/academic_calendar.json. Returns null while loading, or if no final
 * exam period is in the past yet.
 */
export function useSemesterCompletion(): { completion: SemesterCompletion | null; isLoading: boolean } {
  const { events, isLoading } = useAcademicEvents();

  const completion = useMemo(() => {
    if (isLoading) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const finished = events
      .filter(event => /final\s*term\s*exam/i.test(event.title))
      .map(event => {
        const endedOn = parseDateRangeEnd(event.startDate);
        if (!endedOn) return null;
        endedOn.setHours(23, 59, 59, 999);

        const label = event.title
          .replace(/final\s*term\s*examination?/i, '')
          .trim() || event.program || 'your final semester';

        return { label, endedOn };
      })
      .filter((entry): entry is SemesterCompletion => entry !== null && entry.endedOn < today);

    if (finished.length === 0) return null;

    return finished.reduce((latest, entry) => (entry.endedOn > latest.endedOn ? entry : latest));
  }, [events, isLoading]);

  return { completion, isLoading };
}

/**
 * Has this student finished their degree? True for semester VIII once that
 * semester's finals are over. Unlike useGraduationNotice this ignores the
 * dismissal flag — it describes a permanent state, not a one-time card, so
 * screens that have no schedule left to show can rely on it.
 */
export function useHasGraduated(preferences?: UserPreferences | null) {
  const { completion, isLoading: isCalendarLoading } = useSemesterCompletion();
  const programs = usePrograms();
  const isProgramLoading = useIsLoading();
  const isLoading = isCalendarLoading || isProgramLoading;
  const graduated = !isLoading && programs.length > 0 &&
    hasCompletedFinalSemester(programs, preferences, completion);
  return {
    graduated,
    semesterLabel: graduated ? completion?.label ?? null : null,
    isLoading,
  };
}

function readSeen(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Whether to show the graduation card to this student: they're in semester VIII
 * and that semester's finals are over. Shown once per semester label, so a
 * later cohort reaching semester VIII gets it again.
 */
export function useGraduationNotice(preferences?: UserPreferences | null) {
  const { graduated, semesterLabel } = useHasGraduated(preferences);
  const [seen, setSeen] = useState<string[]>(readSeen);

  const dismiss = useCallback(() => {
    if (!semesterLabel) return;
    setSeen(prev => {
      if (prev.includes(semesterLabel)) return prev;
      const next = [...prev, semesterLabel];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Private-browsing / quota — the card just reappears next visit.
      }
      return next;
    });
  }, [semesterLabel]);

  const show =
    graduated && semesterLabel !== null && !seen.includes(semesterLabel);

  return { show, semesterLabel, dismiss };
}
